import { authClient, ApiError, unwrap } from '@/shared/api-client';
import type { PermissionScope } from './permissionScope';

export type Permission = {
  code: string;
  module: string;
  action: string;
  scope: PermissionScope;
};
export type AuthUser = {
  id: string;
  email: string;
  name: string;
  mustChangePassword: boolean;
};
/** A Venue the current user may enter, delivered on the boot call (ADR 0015). */
export type MeVenue = {
  id: string;
  name: string;
  /** The Venue's ISO currency, used to price its InventoryItems and Passes (ADR 0041). */
  currency?: string;
  timezone: string;
  slotGranularityMinutes?: number;
};
export type AuthMe = { user: AuthUser; permissions: Permission[]; venues: MeVenue[] };

/**
 * Thrown when the session projection reports no authenticated user. Extends {@link ApiError}
 * (carrying the 401) while keeping its identity: AuthContext branches on
 * `instanceof UnauthenticatedError` to stop retrying and render the login UI.
 */
export class UnauthenticatedError extends ApiError {
  constructor() {
    super(401, null, 'Unauthenticated');
    this.name = 'UnauthenticatedError';
  }
}

export async function getMe(): Promise<AuthMe> {
  try {
    const me = (await unwrap(authClient.GET('/api/v1/auth/me'))) as AuthMe;
    return { ...me, venues: me.venues ?? [] };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      throw new UnauthenticatedError();
    }
    throw error;
  }
}
